
import * as util from 'util'

import * as big from '../big'
import * as int64 from '../int64'
import * as uuid from '../uuid'

import * as fbe from '../fbe'
import * as core from '../core'
import * as inner from '../inner'

const Big = big.Big // eslint-disable-line
const Int64 = int64.Int64 // eslint-disable-line
const UInt64 = int64.UInt64 // eslint-disable-line
const UUID = uuid.UUID // eslint-disable-line
import {AccountMarginStable} from '../entity/AccountMarginStable';
import {FieldModelAccountMarginStable} from '../entity/AccountMarginStable';
import {UpdateType} from '../core/UpdateType';
import {FieldModelUpdateType} from '../core/UpdateType';

/**
 * MarginChange struct
 */
class MarginChange {
  /**
   * Initialize struct
   * @param {!AccountMarginStable=} accountMargin
   * @param {!UpdateType=} updateType
   * @constructor
   */
  constructor (accountMargin = new AccountMarginStable(), updateType = new UpdateType()) {
    this.AccountMargin = accountMargin
    this.UpdateType = updateType
  }

  /**
   * Copy struct (shallow copy)
   * @this {!MarginChange}
   * @param {!MarginChange} other Other struct
   * @returns {!MarginChange} This struct
   */
  copy (other) {
    if (other.AccountMargin != null) {
      this.AccountMargin = AccountMarginStable.fromObject(other.AccountMargin)
    } else {
      this.AccountMargin = null
    }
    if (other.UpdateType != null) {
      this.UpdateType = UpdateType.fromObject(other.UpdateType)
    } else {
      this.UpdateType = null
    }
    return this
  }

  /**
   * Clone struct (deep clone)
   * @this {!MarginChange}
   * @returns {!MarginChange} Cloned struct
   */
  clone () {
    // Serialize the struct to the FBE stream
    let writer = new MarginChangeModel(new fbe.WriteBuffer())
    writer.serialize(this)

    // Deserialize the struct from the FBE stream
    let reader = new MarginChangeModel(new fbe.ReadBuffer())
    reader.attachBuffer(writer.buffer)
    return reader.deserialize().value
  }

  /**
   * Is this struct equal to other one?
   * @this {!MarginChange}
   * @param {!MarginChange} other Other struct
   * @returns {boolean} Equal result
   */
  eq (other) {
    if (!(other instanceof MarginChange)) {
      throw new TypeError('Instance of MarginChange is required!')
    }
    return true
  }

  /**
   * Convert struct to JSON
   * @this {!MarginChange}
   * @returns {!object} Struct value for JSON
   */
  toJSON () {
    return {
      AccountMargin: ((this.AccountMargin != null) ? this.AccountMargin : null),
      UpdateType: ((this.UpdateType != null) ? this.UpdateType : null)
    }
  }

  /**
   * Convert JSON to struct
   * @param {!string} json JSON string
   * @returns {!object} Struct value for JSON
   */
  static fromJSON (json) {
    return MarginChange.fromObject(JSON.parse(json))
  }

  /**
   * Create struct from object value
   * @param {!MarginChange} other Object value
   * @returns {!MarginChange} Created struct
   */
  static fromObject (other) {
    return new MarginChange().copy(other)
  }

  /**
   * Get the FBE type
   * @this {!MarginChange}
   * @returns {!number} FBE type
   */
  get fbeType () {
    return MarginChange.fbeType
  }

  /**
   * Get the FBE type (static)
   * @this {!MarginChange}
   * @returns {!number} FBE type
   */
  static get fbeType () {
    return 4201
  }
}

export { MarginChange };

/**
 * Fast Binary Encoding MarginChange field model
 */
class FieldModelMarginChange extends fbe.FieldModel {
  /**
   * Initialize field model with the given buffer and offset
   * @param {!fbe.ReadBuffer|!fbe.WriteBuffer} buffer Buffer
   * @param {!number} offset Offset
   * @constructor
   */
  constructor (buffer, offset) {
    super(buffer, offset)
    this.AccountMargin = new FieldModelAccountMarginStable(buffer, 4 + 4)
    this.UpdateType = new FieldModelUpdateType(buffer, this.AccountMargin.fbeOffset + this.AccountMargin.fbeSize)
  }

  /**
   * Get the field size
   * @this {!FieldModelMarginChange}
   * @returns {!number} Field size
   */
  get fbeSize () {
    return 4
  }

  /**
   * Get the field body size
   * @this {!FieldModelMarginChange}
   * @returns {!number} Field body size
   */
  get fbeBody () {
    return 4 + 4 + this.AccountMargin.fbeSize + this.UpdateType.fbeSize
  }

  /**
   * Get the field extra size
   * @this {!FieldModelMarginChange}
   * @returns {!number} Field extra size
   */
  get fbeExtra () {
    if ((this._buffer.offset + this.fbeOffset + this.fbeSize) > this._buffer.size) {
      return 0
    }

    let fbeStructOffset = this.readUInt32(this.fbeOffset)
    if ((fbeStructOffset === 0) || ((this._buffer.offset + fbeStructOffset + 4) > this._buffer.size)) {
      return 0
    }

    this._buffer.shift(fbeStructOffset)

    let fbeResult = this.fbeBody + this.AccountMargin.fbeExtra + this.UpdateType.fbeExtra

    this._buffer.unshift(fbeStructOffset)

    return fbeResult
  }

  /**
   * Get the field type
   * @this {!FieldModelMarginChange}
   * @returns {!number} Field type
   */
  get fbeType () {
    return FieldModelMarginChange.fbeType
  }

  /**
   * Get the field type (static)
   * @this {!FieldModelMarginChange}
   * @returns {!number} Field type
   */
  static get fbeType () {
    return 4201
  }

  /**
   * Check if the struct value is valid
   * @this {!FieldModelMarginChange}
   * @param {!boolean} fbeVerifyType Verify model type flag, defaults is true
   * @returns {!boolean} Field model valid state
   */
  verify (fbeVerifyType = true) {
    if ((this._buffer.offset + this.fbeOffset + this.fbeSize) > this._buffer.size) {
      return true
    }

    let fbeStructOffset = this.readUInt32(this.fbeOffset)
    if ((fbeStructOffset === 0) || ((this._buffer.offset + fbeStructOffset + 4 + 4) > this._buffer.size)) {
      return false
    }

    let fbeStructSize = this.readUInt32(fbeStructOffset)
    if (fbeStructSize < (4 + 4)) {
      return false
    }

    let fbeStructType = this.readUInt32(fbeStructOffset + 4)
    if (fbeVerifyType && (fbeStructType !== this.fbeType)) {
      return false
    }

    this._buffer.shift(fbeStructOffset)
    let fbeResult = this.verifyFields(fbeStructSize)
    this._buffer.unshift(fbeStructOffset)
    return fbeResult
  }

  /**
   * Check if the struct fields are valid
   * @this {!FieldModelMarginChange}
   * @param {!number} fbeStructSize FBE struct size
   * @returns {!boolean} Field model valid state
   */
  verifyFields (fbeStructSize) {
    let fbeCurrentSize = 4 + 4

    if ((fbeCurrentSize + this.AccountMargin.fbeSize) > fbeStructSize) {
      return true
    }
    if (!this.AccountMargin.verify()) {
      return false
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.AccountMargin.fbeSize

    if ((fbeCurrentSize + this.UpdateType.fbeSize) > fbeStructSize) {
      return true
    }
    if (!this.UpdateType.verify()) {
      return false
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.UpdateType.fbeSize

    return true
  }

  /**
   * Get the struct value (begin phase)
   * @this {!FieldModelMarginChange}
   * @returns {!number} Field model begin offset
   */
  getBegin () {
    if ((this._buffer.offset + this.fbeOffset + this.fbeSize) > this._buffer.size) {
      return 0
    }

    let fbeStructOffset = this.readUInt32(this.fbeOffset)
    console.assert((fbeStructOffset > 0) && ((this._buffer.offset + fbeStructOffset + 4 + 4) <= this._buffer.size), 'Model is broken!')
    if ((fbeStructOffset === 0) || ((this._buffer.offset + fbeStructOffset + 4 + 4) > this._buffer.size)) {
      return 0
    }

    let fbeStructSize = this.readUInt32(fbeStructOffset)
    console.assert((fbeStructSize >= (4 + 4)), 'Model is broken!')
    if (fbeStructSize < (4 + 4)) {
      return 0
    }

    this._buffer.shift(fbeStructOffset)
    return fbeStructOffset
  }

  /**
   * Get the struct value (end phase)
   * @this {!FieldModelMarginChange}
   * @param {!number} fbeBegin Field model begin offset
   */
  getEnd (fbeBegin) {
    this._buffer.unshift(fbeBegin)
  }

  /**
   * Get the struct value
   * @this {!FieldModelMarginChange}
   * @param {!MarginChange} fbeValue Default value, defaults is new MarginChange()
   * @returns {!MarginChange} MarginChange value
   */
  get (fbeValue = new MarginChange()) {
    let fbeBegin = this.getBegin()
    if (fbeBegin === 0) {
      return fbeValue
    }

    let fbeStructSize = this.readUInt32(0)
    this.getFields(fbeValue, fbeStructSize)
    this.getEnd(fbeBegin)
    return fbeValue
  }

  /**
   * Get the struct fields values
   * @this {!FieldModelMarginChange}
   * @param {!MarginChange} fbeValue MarginChange value
   * @param {!number} fbeStructSize Struct size
   */
  getFields (fbeValue, fbeStructSize) {
    let fbeCurrentSize = 4 + 4

    if ((fbeCurrentSize + this.AccountMargin.fbeSize) <= fbeStructSize) {
      fbeValue.AccountMargin = this.AccountMargin.get()
    } else {
      fbeValue.AccountMargin = new AccountMarginStable()
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.AccountMargin.fbeSize

    if ((fbeCurrentSize + this.UpdateType.fbeSize) <= fbeStructSize) {
      fbeValue.UpdateType = this.UpdateType.get()
    } else {
      fbeValue.UpdateType = new UpdateType()
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.UpdateType.fbeSize
  }

  /**
   * Set the struct value (begin phase)
   * @this {!FieldModelMarginChange}
   * @returns {!number} Field model begin offset
   */
  setBegin () {
    console.assert(((this._buffer.offset + this.fbeOffset + this.fbeSize) <= this._buffer.size), 'Model is broken!')
    if ((this._buffer.offset + this.fbeOffset + this.fbeSize) > this._buffer.size) {
      return 0
    }

    let fbeStructSize = this.fbeBody
    let fbeStructOffset = this._buffer.allocate(fbeStructSize) - this._buffer.offset
    console.assert((fbeStructOffset > 0) && ((this._buffer.offset + fbeStructOffset + fbeStructSize) <= this._buffer.size), 'Model is broken!')
    if ((fbeStructOffset <= 0) || ((this._buffer.offset + fbeStructOffset + fbeStructSize) > this._buffer.size)) {
      return 0
    }

    this.writeUInt32(this.fbeOffset, fbeStructOffset)
    this.writeUInt32(fbeStructOffset, fbeStructSize)
    this.writeUInt32(fbeStructOffset + 4, this.fbeType)

    this._buffer.shift(fbeStructOffset)
    return fbeStructOffset
  }

  /**
   * Set the struct value (end phase)
   * @this {!FieldModelMarginChange}
   * @param {!number} fbeBegin Field model begin offset
   */
  setEnd (fbeBegin) {
    this._buffer.unshift(fbeBegin)
  }

  /**
   * Set the struct value
   * @this {!FieldModelMarginChange}
   * @param {!MarginChange} fbeValue MarginChange value
   */
  set (fbeValue) {
    let fbeBegin = this.setBegin()
    if (fbeBegin === 0) {
      return
    }

    this.setFields(fbeValue)
    this.setEnd(fbeBegin)
  }

  /**
   * Set the struct fields values
   * @this {!FieldModelMarginChange}
   * @param {MarginChange} fbeValue MarginChange value
   */
  setFields (fbeValue) {
    this.AccountMargin.set(fbeValue.AccountMargin)
    this.UpdateType.set(fbeValue.UpdateType)
  }
}

export { FieldModelMarginChange };

/**
 * Fast Binary Encoding MarginChange model
 */
class MarginChangeModel extends fbe.Model {
  /**
   * Initialize model with the given buffer
   * @param {!fbe.ReadBuffer|!fbe.WriteBuffer} buffer Read/Write buffer, defaults is new fbe.WriteBuffer()
   * @constructor
   */
  constructor (buffer = new fbe.WriteBuffer()) {
    super(buffer)
    this._model = new FieldModelMarginChange(this.buffer, 4)
  }

  /**
   * Get the MarginChange model
   * @this {!MarginChangeModel}
   * @returns {!FieldModelMarginChange} model MarginChange model
   */
  get model () {
    return this._model
  }

  /**
   * Get the model size
   * @this {!MarginChangeModel}
   * @returns {!number} Model size
   */
  get fbeSize () {
    return this.model.fbeSize + this.model.fbeExtra
  }

  /**
   * Get the model type
   * @this {!MarginChangeModel}
   * @returns {!number} Model type
   */
  get fbeType () {
    return MarginChangeModel.fbeType
  }

  /**
   * Get the model type (static)
   * @this {!MarginChangeModel}
   * @returns {!number} Model type
   */
  static get fbeType () {
    return FieldModelMarginChange.fbeType
  }

  /**
   * Check if the struct value is valid
   * @this {!MarginChangeModel}
   * @returns {!boolean} Model valid state
   */
  verify () {
    if ((this.buffer.offset + this.model.fbeOffset - 4) > this.buffer.size) {
      return false
    }

    let fbeFullSize = this.readUInt32(this.model.fbeOffset - 4)
    if (fbeFullSize < this.model.fbeSize) {
      return false
    }

    return this.model.verify()
  }

  /**
   * Create a new model (begin phase)
   * @this {!MarginChangeModel}
   * @returns {!number} Model begin offset
   */
  createBegin () {
    return this.buffer.allocate(4 + this.model.fbeSize)
  }

  /**
   * Create a new model (end phase)
   * @this {!MarginChangeModel}
   * @param {!number} fbeBegin Model begin offset
   */
  createEnd (fbeBegin) {
    let fbeEnd = this.buffer.size
    let fbeFullSize = fbeEnd - fbeBegin
    this.writeUInt32(this.model.fbeOffset - 4, fbeFullSize)
    return fbeFullSize
  }

  /**
   * Serialize the struct value
   * @this {!MarginChangeModel}
   * @param {!MarginChange} value MarginChange value
   * @return {!number} Model begin offset
   */
  serialize (value) {
    let fbeBegin = this.createBegin()
    this.model.set(value)
    return this.createEnd(fbeBegin)
  }

  /**
   * Deserialize the struct value
   * @this {!MarginChangeModel}
   * @param {!MarginChange} value MarginChange value, defaults is new MarginChange()
   * @return {!object} Deserialized MarginChange value and its size
   */
  deserialize (value = new MarginChange()) {
    if ((this.buffer.offset + this.model.fbeOffset - 4) > this.buffer.size) {
      return { value: new MarginChange(), size: 0 }
    }

    let fbeFullSize = this.readUInt32(this.model.fbeOffset - 4)
    console.assert((fbeFullSize >= this.model.fbeSize), 'Model is broken!')
    if (fbeFullSize < this.model.fbeSize) {
      return { value: new MarginChange(), size: 0 }
    }

    this.model.get(value)
    return { value: value, size: fbeFullSize }
  }

  /**
   * Move to the next struct value
   * @this {!MarginChangeModel}
   * @param {!number} prev Previous MarginChange model size
   */
  next (prev) {
    this.model.fbeShift(prev)
  }
}

export { MarginChangeModel };
